import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const modalVariants = cva(
  'fixed inset-0 z-50 flex items-center justify-center',
  {
    variants: {
      size: {
        sm: 'p-4',
        default: 'p-4 md:p-6',
        lg: 'p-4 md:p-8',
        xl: 'p-4 md:p-12',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const modalOverlayVariants = cva(
  'fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity'
);

const modalContentVariants = cva(
  'relative bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden w-full transition-all',
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        default: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface ModalProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof modalVariants> {
  isOpen: boolean;
  onClose: () => void;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  children: React.ReactNode;
}

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  ({
    isOpen,
    onClose,
    closeOnOverlayClick = true,
    closeOnEsc = true,
    size,
    className,
    children,
    ...props
  }, ref) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Handle ESC key
    useEffect(() => {
      if (!isOpen || !closeOnEsc) return;

      const handleEsc = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, closeOnEsc, onClose]);

    // Handle focus management
    useEffect(() => {
      if (!isOpen) return;

      // Store previously focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Focus the modal content
      if (modalRef.current) {
        modalRef.current.focus();
      }

      // Return focus when modal closes
      return () => {
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }, [isOpen]);

    // Prevent body scroll when modal is open
    useEffect(() => {
      if (!isOpen) return;

      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }, [isOpen]);

    // Handle overlay click
    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && event.target === event.currentTarget) {
        onClose();
      }
    };

    if (!isOpen) return null;

    const modalContent = (
      <div className={cn(modalVariants({ size }), className)} {...props}>
        <div
          className={modalOverlayVariants()}
          onClick={handleOverlayClick}
          aria-hidden="true"
          data-testid="modal-overlay"
        />
        <div
          ref={(element) => {
            if (typeof ref === 'function') {
              ref(element);
            } else if (ref) {
              ref.current = element;
            }
            modalRef.current = element;
          }}
          className={modalContentVariants({ size })}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    );

    // Render modal in portal
    return createPortal(modalContent, document.body);
  }
);

Modal.displayName = 'Modal';